import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getImageUrl } from '../../../../utils/imageUrl';
import { CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import VerifyPropertyModal from '../../../../components/VerifyPropertyModal/VerifyPropertyModal';

import { useUserContext } from '../../../protectedRoute/UserContext';

interface PropertyCardProps {
  id: string;
  name: string;
  address: string;
  images: { image?: string; is_primary?: boolean }[];
  status: string;
  beds: number;
  is_verified: boolean;
  propertyVerificationStatus: string; // New prop for property verification status
  onPropertyVerified: () => void; // New prop for callback
}

const PropertyCard: React.FC<PropertyCardProps> = ({ id, name, address, images, status, beds, is_verified, propertyVerificationStatus, onPropertyVerified }) => {
  const navigate = useNavigate();
  const { dispatch } = useUserContext(); // Get dispatch
  
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false); // State for modal
  const DEFAULT_IMAGE_URL = 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?q=80&w=1974&auto=format&fit=crop';
  // const imageUrl = images && images.length > 0 && images[0].image ? `${process.env.REACT_APP_API_ENDPOINT}${images[0].image}` : DEFAULT_IMAGE_URL;

  const primaryImage = images?.find(img => img.is_primary)?.image || images?.[0]?.image;
  const imageUrl = primaryImage ? getImageUrl(primaryImage) : DEFAULT_IMAGE_URL;

  const handleCardClick = () => {
    dispatch({ type: 'SET_PROPERTY_ID', payload: id }); // Dispatch property ID on click

    if (is_verified) {
      navigate(`/host/property/${id}`);
    } else {
      // No dialog, just navigate or do nothing
    }
  };

  const handleOpenVerifyModal = () => {
    setIsVerifyModalOpen(true);
  };

  const handleCloseVerifyModal = () => {
    setIsVerifyModalOpen(false);
  };

  const handleVerificationSuccess = () => {
    onPropertyVerified(); // Call the parent callback to refresh properties
    handleCloseVerifyModal();
  };

  return (
    <>
      <div
      key={id}
      className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-card rounded-lg border border-border hover:shadow-md hover:border-primary/30 transition-all cursor-pointer"
      onClick={handleCardClick}
    >
      <img
        src={imageUrl}
        alt={name}
        className="w-full sm:w-24 h-32 sm:h-16 object-cover rounded-lg"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-foreground">{name}</h3>
          {is_verified ? (
            <CheckCircle size={20} color="green" />
          ) : propertyVerificationStatus === 'PENDING' ? (
            <p className="text-sm text-yellow-500 ml-2">
              Verification Pending
            </p>
          ) : (
            <XCircle size={20} color="red" />
          )}
        </div>
        <p className="text-sm text-muted-foreground mb-2">{address}</p>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center text-sm">
          <Badge className={`${status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'} mr-2`}>{status}</Badge>
          </div>
          {!is_verified && propertyVerificationStatus !== 'PENDING' && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenVerifyModal();
              }}
              className="ml-4"
            >
              VERIFY PROPERTY
            </Button>
          )}
        </div>
      </div>
    </div>
      
      <VerifyPropertyModal
        open={isVerifyModalOpen}
        onClose={handleCloseVerifyModal}
        propertyId={id}
        onVerificationSuccess={handleVerificationSuccess}
      />
    </>
  );
};

export default PropertyCard;